import { useEffect, useRef, useState } from 'react';

export function useHoverPause(ref: React.RefObject<HTMLElement>) {
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const handleMouseEnter = () => setIsPaused(true);
    const handleMouseLeave = () => setIsPaused(false);

    node.addEventListener('mouseenter', handleMouseEnter);
    node.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      node.removeEventListener('mouseenter', handleMouseEnter);
      node.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [ref]);

  return isPaused;
}

export function useAutoProgressFade(
  text: string,
  isPaused: boolean,
  onClear: () => void,
  duration = 8000,
  intervalMs = 30
) {
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const [displayText, setDisplayText] = useState(text);
  const progressRef = useRef(0); // Persist progress value across renders
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Handle text changes (reset progress only on new text)
  useEffect(() => {
    if (!text) return;
    setDisplayText(text);
    setProgress(0);
    progressRef.current = 0;
    setVisible(true);
  }, [text]);

  // Handle the progress interval and pause logic
  useEffect(() => {
    if (!visible) return;

    const step = (100 / duration) * intervalMs;

    // Clear any previous interval
    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      if (!isPaused) {
        progressRef.current += step;
        if (progressRef.current >= 100) {
          progressRef.current = 100;
          setVisible(false); // Fade out
          onClear();
          clearInterval(intervalRef.current!);
        }
        setProgress(progressRef.current);
      }
    }, intervalMs);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPaused, visible, duration, intervalMs]);

  return { progress, visible, displayText };
}
